import type { SimConfig } from '../config';
import { campIndex, opponentOf } from '../constants';
import type { EventSink } from '../events';
import type { MatchState } from '../types';
import type { PendingDamage } from './bombs';

/**
 * Applies the tick's damage in stable order (explosion id, then totems, then players).
 * Every rule here is camp-symmetric: the camp hit suffers the consequence, whoever armed the ball.
 * Player hits stun. Totem hits remove one life. Neither awards a point.
 */
export function applyDamage(
  state: MatchState,
  config: SimConfig,
  pending: readonly PendingDamage[],
  sink: EventSink,
): void {
  const tick = sink.tick;
  for (const damage of pending) {
    const scorer = opponentOf(damage.camp);
    if (damage.kind === 'TOTEM') {
      const totem = state.totems[campIndex(damage.camp)];
      // A destroyed totem is no longer a valid target.
      if (totem.hp <= 0) continue;
      totem.hp -= 1;
      state.stats.totemHits += 1;
      state.stats.firstTotemHitTick ??= tick;
      sink.emit({
        type: 'TOTEM_HIT',
        explosionId: damage.explosion.id,
        camp: damage.camp,
        scorer,
        hpAfter: totem.hp,
      });
      if (totem.hp === 1) sink.emit({ type: 'TOTEM_CRITICAL', camp: damage.camp });
      if (totem.hp === 0) {
        totem.destroyedAtTick = tick;
        sink.emit({ type: 'TOTEM_DESTROYED', camp: damage.camp });
      }
    } else {
      const player = state.players[campIndex(damage.camp)];
      // Re-checked at application time: a ball stunned earlier this tick is not stunned again.
      if (player.status !== 'NORMAL') continue;
      player.status = 'STUNNED';
      player.vx = 0;
      player.vy = 0;
      player.stunUntilTick = tick + 1 + config.ticks.stun;
      player.protectedUntilTick = player.stunUntilTick + config.ticks.protection;
      const selfArmed = damage.explosion.lastToucher === damage.camp;
      state.stats.playerHits += 1;
      state.stats.firstPlayerHitTick ??= tick;
      if (selfArmed) state.stats.selfHits += 1;
      sink.emit({
        type: 'PLAYER_HIT',
        explosionId: damage.explosion.id,
        camp: damage.camp,
        scorer,
        selfArmed,
      });
      sink.emit({ type: 'PLAYER_STUNNED', camp: damage.camp, untilTick: player.stunUntilTick });
    }
  }
}
